import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createGlobalKey } from "@aviv-unified-frontend/internal";

import { useElapsed } from "../../../utils/useElapsed";
import { useOverlayOrchestrator } from "../../OverlayOrchestrator";
import { useScrolled } from "../../../utils/useScrolled";

import { useCustomBannerTracking } from "./useCustomBannerTracking";

type BannerData = {
  dismiss: {
    count: number;
    at: number;
  };
};

export const defaultBannerData: BannerData = {
  dismiss: {
    count: 0,
    at: Date.now(),
  },
};

export const CUSTOM_BANNER_STORAGE_KEY =
  createGlobalKey("customBanner").toLowerCase();

export const getLocalBannerData = (): BannerData => {
  const data = localStorage.getItem(CUSTOM_BANNER_STORAGE_KEY);

  try {
    return data ? JSON.parse(data) : defaultBannerData;
  } catch {
    return defaultBannerData;
  }
};

export const setLocalBannerData = (data: BannerData) => {
  localStorage.setItem(CUSTOM_BANNER_STORAGE_KEY, JSON.stringify(data));
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const HIDE_DURATION: Record<"mobile" | "desktop", Record<number, number>> = {
  mobile: {
    1: DAY_IN_MS,
    2: 2 * DAY_IN_MS,
    3: 3 * DAY_IN_MS,
    4: 7 * DAY_IN_MS,
  },
  desktop: {
    1: 7 * DAY_IN_MS,
    2: 7 * DAY_IN_MS,
    3: 7 * DAY_IN_MS,
    4: 7 * DAY_IN_MS,
  },
};

export const getHideDuration = (count: number, isSmallViewport: boolean) =>
  HIDE_DURATION[isSmallViewport ? "mobile" : "desktop"][count] ?? null;

export const shouldShowBanner = (
  data: BannerData,
  isSmallViewport: boolean,
): boolean => {
  const { at, count } = data.dismiss;

  if (count === 0) return true;

  const hideDuration = getHideDuration(count, isSmallViewport);

  if (!hideDuration) return false;

  return Date.now() - at >= hideDuration;
};

const KEY = "custom-banner";

export const useCustomBanner = ({
  isSmallViewport,
  isMobileOrTablet,
}: {
  isSmallViewport: boolean;
  isMobileOrTablet: boolean;
}) => {
  const { dispatch } = useOverlayOrchestrator();

  const { onDisplayEvent, onClickEvent, onDismissEvent } =
    useCustomBannerTracking();

  // The initial state is set to null to avoid any flickering issues related to the use of BreakpointValue.
  const [canOpenModal, setCanOpenModal] = useState<boolean | null>(null);

  const [bannerData, setBannerData] = useState<BannerData | undefined>(
    undefined,
  );

  const isLocalStorageUpdated = useRef(false);

  const isScrolled = useScrolled({
    distance: 300,
    active: Boolean(canOpenModal) && isSmallViewport,
  });

  /**
   * Check if the elapsed time has passed before checking the banner visibility.
   * As the hide duration is different for mobile and desktop, this ensure that isSmallViewport is the correct value & not the fallback one.
   */
  const isElapsed = useElapsed({
    duration: 5,
    active: true,
  });

  const shouldShow = useMemo(() => {
    return bannerData ? shouldShowBanner(bannerData, isSmallViewport) : null;
  }, [bannerData, isSmallViewport]);

  useEffect(() => {
    setBannerData(getLocalBannerData());
  }, []);

  useEffect(() => {
    if (shouldShow === false || !isMobileOrTablet) {
      dispatch({ type: "DISABLE", payload: KEY });

      setCanOpenModal(false);

      return;
    }

    setCanOpenModal(Boolean(shouldShow) && isElapsed);
  }, [isMobileOrTablet, isElapsed, shouldShow, dispatch]);

  useEffect(() => {
    if (isScrolled) {
      setCanOpenModal(false);
      dispatch({ type: "COMPLETE", payload: KEY });
    }
  }, [isScrolled, dispatch]);

  useEffect(() => {
    if (canOpenModal) {
      onDisplayEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canOpenModal]);

  /***
   * Update the local banner data in local storage.
   * This function is called when the user interacts with the banner (e.g., clicks a link or dismisses the banner).
   *
   * Update is done only once to avoid incrementing the count multiple times.
   */
  const onUpdateBannerData = useCallback(() => {
    if (!bannerData || isLocalStorageUpdated.current) return;

    const nextData = {
      dismiss: {
        count: bannerData.dismiss.count + 1,
        at: Date.now(),
      },
    };

    setLocalBannerData(nextData);
    isLocalStorageUpdated.current = true;
  }, [bannerData]);

  const onClick = useCallback(
    (link_text: string) => {
      onUpdateBannerData();
      onClickEvent(link_text);
    },
    [onUpdateBannerData, onClickEvent],
  );

  const onDismiss = useCallback(() => {
    /**
     * Dismiss the banner and update local storage.
     */
    setCanOpenModal(false);
    onUpdateBannerData();
    onDismissEvent();
    dispatch({ type: "COMPLETE", payload: KEY });
  }, [onUpdateBannerData, onDismissEvent, dispatch]);

  return {
    canOpenModal,
    onDismiss,
    onClick,
  };
};
